import React, { PureComponent } from "react"
import classNames from "classnames"
import { Mutation } from "react-apollo"

import FounderActionCard from "../founder-action-card/founder-action-card"
import {
  RESTORE_FOUNDER_ACTION,
  UPSERT_FOUNDER_ACTION,
  MY_FOUNDER_ASSIST_QUERY,
} from "../../lib/founder-assist-queries"

import "./founder-actions-list.scss"

const PRIORITY_RANK = {
  high: 0,
  medium: 1,
  low: 2,
  unprioritized: 3,
}

function sortOpen(a, b) {
  const pa = PRIORITY_RANK[a.priority || "unprioritized"]
  const pb = PRIORITY_RANK[b.priority || "unprioritized"]
  if (pa !== pb) return pa - pb
  const ia = Number.isFinite(a.impactScore) ? a.impactScore : 0
  const ib = Number.isFinite(b.impactScore) ? b.impactScore : 0
  if (ib !== ia) return ib - ia
  return new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
}

function sortRecentCompleted(a, b) {
  const ta = a.completedAt ? new Date(a.completedAt).getTime() : 0
  const tb = b.completedAt ? new Date(b.completedAt).getTime() : 0
  return tb - ta
}

function countByPriority(actions) {
  const counts = { high: 0, medium: 0, low: 0, unprioritized: 0 }
  for (const a of actions) {
    const p = a.priority || "unprioritized"
    if (counts[p] !== undefined) counts[p]++
  }
  return counts
}

class FounderActionsList extends PureComponent {
  constructor(props) {
    super(props)
    this.state = {
      completedOpen: false,
      undoAction: null,
      adding: false,
      newTitle: "",
    }
    this._undoTimer = null
    this._addRef = React.createRef()
  }

  componentDidMount() {
    this._mounted = true
  }

  componentWillUnmount() {
    this._mounted = false
    if (this._undoTimer) clearTimeout(this._undoTimer)
  }

  safeSetState(state) {
    if (this._mounted) this.setState(state)
  }

  handleArchived = (action) => {
    if (this._undoTimer) clearTimeout(this._undoTimer)
    this.safeSetState({ undoAction: action })
    this._undoTimer = setTimeout(() => {
      this.safeSetState((p) =>
        p.undoAction && p.undoAction.id === action.id
          ? { undoAction: null }
          : null
      )
    }, 6000)
  }

  clearUndo = () => {
    if (this._undoTimer) clearTimeout(this._undoTimer)
    this.safeSetState({ undoAction: null })
  }

  startAdding = () => {
    this.setState({ adding: true, newTitle: "" }, () => {
      if (this._addRef.current) this._addRef.current.focus()
    })
  }

  cancelAdding = () => {
    this.setState({ adding: false, newTitle: "" })
  }

  submitNewAction = (save) => {
    const trimmed = this.state.newTitle.trim()
    if (!trimmed) {
      this.cancelAdding()
      return
    }
    save({ variables: { title: trimmed } }).then(() => {
      this.safeSetState({ adding: false, newTitle: "" })
    })
  }

  render() {
    const { actions } = this.props
    const open = (actions || [])
      .filter((a) => a.status === "open")
      .sort(sortOpen)
    const completed = (actions || [])
      .filter((a) => a.status === "archived")
      .sort(sortRecentCompleted)
    const { completedOpen, undoAction, adding, newTitle } = this.state

    const counts = countByPriority(open)
    const breakdownParts = []
    if (counts.high) breakdownParts.push({ label: "high", n: counts.high, key: "high" })
    if (counts.medium) breakdownParts.push({ label: "medium", n: counts.medium, key: "medium" })
    if (counts.low) breakdownParts.push({ label: "low", n: counts.low, key: "low" })
    if (counts.unprioritized)
      breakdownParts.push({
        label: "unprioritized",
        n: counts.unprioritized,
        key: "unprioritized",
      })

    return (
      <div className="founder-actions-list">
        <header className="founder-actions-list__header">
          <div className="founder-actions-list__header-text">
            <h2 className="founder-actions-list__eyebrow">
              This week&rsquo;s plan
            </h2>
            {open.length > 0 ? (
              <p className="founder-actions-list__breakdown">
                {breakdownParts.map((part, idx) => (
                  <React.Fragment key={part.key}>
                    {idx > 0 && (
                      <span className="founder-actions-list__breakdown-sep">·</span>
                    )}
                    <span
                      className={classNames(
                        "founder-actions-list__breakdown-part",
                        `founder-actions-list__breakdown-part--${part.key}`
                      )}
                    >
                      <strong>{part.n}</strong> {part.label}
                    </span>
                  </React.Fragment>
                ))}
                {completed.length > 0 && (
                  <>
                    <span className="founder-actions-list__breakdown-sep">·</span>
                    <span className="founder-actions-list__breakdown-completed">
                      <strong>{completed.length}</strong> completed
                    </span>
                  </>
                )}
              </p>
            ) : (
              <p className="founder-actions-list__breakdown founder-actions-list__breakdown--empty">
                No open actions yet
              </p>
            )}
          </div>
          {!adding && (
            <button
              type="button"
              className="founder-actions-list__add-button"
              onClick={this.startAdding}
            >
              + Add my own
            </button>
          )}
        </header>

        {adding && (
          <Mutation
            mutation={UPSERT_FOUNDER_ACTION}
            refetchQueries={[{ query: MY_FOUNDER_ASSIST_QUERY }]}
          >
            {(save, { loading }) => (
              <div className="founder-actions-list__add-row">
                <input
                  ref={this._addRef}
                  type="text"
                  value={newTitle}
                  onChange={(e) =>
                    this.setState({ newTitle: e.target.value })
                  }
                  onKeyDown={(e) => {
                    if (e.key === "Enter") this.submitNewAction(save)
                    if (e.key === "Escape") this.cancelAdding()
                  }}
                  placeholder="What action do you want to commit to?"
                  className="founder-actions-list__add-input"
                  maxLength={200}
                  disabled={loading}
                />
                <button
                  type="button"
                  className="founder-actions-list__add-confirm"
                  onClick={() => this.submitNewAction(save)}
                  disabled={loading || !newTitle.trim()}
                >
                  Add
                </button>
                <button
                  type="button"
                  className="founder-actions-list__add-cancel"
                  onClick={this.cancelAdding}
                >
                  Cancel
                </button>
              </div>
            )}
          </Mutation>
        )}

        {open.length === 0 ? (
          <div className="founder-actions-list__empty">
            <p>
              Nothing on your action plan yet. Run a quick check-in to get
              fresh suggestions, or add your own.
            </p>
          </div>
        ) : (
          <div className="founder-actions-list__cards" role="list">
            {open.map((action) => (
              <FounderActionCard
                key={action.id}
                action={action}
                onArchived={this.handleArchived}
              />
            ))}
          </div>
        )}

        {completed.length > 0 && (
          <div className="founder-actions-list__completed">
            <button
              type="button"
              className="founder-actions-list__completed-header"
              onClick={() =>
                this.setState((p) => ({ completedOpen: !p.completedOpen }))
              }
              aria-expanded={completedOpen}
            >
              <span>Completed ({completed.length})</span>
              {completed[0] && completed[0].completedAt && (
                <span className="founder-actions-list__completed-date">
                  most recent{" "}
                  {new Date(completed[0].completedAt).toLocaleDateString()}
                </span>
              )}
              <span
                className={classNames("founder-actions-list__chevron", {
                  "founder-actions-list__chevron--open": completedOpen,
                })}
                aria-hidden
              />
            </button>
            {completedOpen && (
              <div className="founder-actions-list__cards" role="list">
                {completed.map((action) => (
                  <FounderActionCard
                    key={action.id}
                    action={action}
                    onArchived={() => {}}
                  />
                ))}
              </div>
            )}
          </div>
        )}

        {undoAction && (
          <Mutation
            mutation={RESTORE_FOUNDER_ACTION}
            refetchQueries={[{ query: MY_FOUNDER_ASSIST_QUERY }]}
          >
            {(restore) => (
              <div className="founder-actions-list__toast" role="status">
                Done. {open.length} left this quarter.
                <button
                  className="founder-actions-list__toast-undo"
                  onClick={() => {
                    restore({ variables: { id: undoAction.id } }).then(
                      this.clearUndo
                    )
                  }}
                >
                  Undo
                </button>
              </div>
            )}
          </Mutation>
        )}
      </div>
    )
  }
}

export default FounderActionsList
